import React from 'react';
import { Phone, ExternalLink, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { crisisResources, supportGroups } from '../data/mockData';

const ResourcesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-orange-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Get Help Now - 24/7 Support Available
          </h1>
          <p className="text-xl text-gray-600">
            You are not alone. Help is available right now, any time of day or night.
          </p>
        </div>
      </section>

      {/* Crisis Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Immediate Crisis Support
          </h2>
          <p className="text-center text-lg text-gray-600 mb-10 max-w-3xl mx-auto">
            If you're in crisis or thinking about suicide, please call one of these numbers immediately
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {crisisResources.map((resource) => (
              <Card key={resource.id} className="border-2 border-red-200 bg-red-50 hover:border-red-400 transition-all duration-300 hover:shadow-xl">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900 flex items-center space-x-2">
                    <Phone className="w-6 h-6 text-red-600" />
                    <span>{resource.name}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-3xl font-bold text-red-700">
                    {resource.phone}
                  </div>
                  <p className="text-gray-700">{resource.description}</p>
                  <div className="text-sm text-gray-600 font-semibold">
                    Available: {resource.available}
                  </div>
                  {resource.phone.includes('-') && (
                    <a
                      href={`tel:${resource.phone}`}
                      className="inline-flex items-center justify-center w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-semibold"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Call Now
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-2 border-red-300 bg-red-50">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Medical Emergency? Call 911
              </h3>
              <p className="text-gray-700 mb-6 text-lg">
                For immediate medical emergencies, overdose, or life-threatening situations
              </p>
              <a
                href="tel:911"
                className="inline-flex items-center justify-center px-10 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-bold text-xl"
              >
                <Phone className="w-6 h-6 mr-3" />
                Call 911
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Support Groups */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Support Groups & Recovery Programs
          </h2>
          <p className="text-center text-lg text-gray-600 mb-10 max-w-3xl mx-auto">
            Find local meetings and connect with communities dedicated to recovery
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {supportGroups.map((group) => (
              <Card key={group.id} className="border-2 border-teal-200 hover:border-teal-400 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">
                    {group.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700 text-lg">{group.description}</p>
                  {group.phone && (
                    <div className="text-lg font-semibold text-gray-900">
                      <Phone className="w-5 h-5 inline mr-2" />
                      {group.phone}
                    </div>
                  )}
                  {group.website && (
                    <a
                      href={group.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors duration-200 font-semibold"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Visit Website
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">
            Additional Resources
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2 border-gray-200">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">
                  Find Treatment Centers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  SAMHSA's treatment locator helps you find substance abuse treatment facilities near you.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Free and confidential service</li>
                  <li>• Available 24/7</li>
                  <li>• English and Spanish</li>
                  <li>• Referrals to local treatment</li>
                </ul>
                <a
                  href="https://findtreatment.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors duration-200 font-semibold"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Find Treatment
                </a>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">
                  Mental Health Support
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Many people benefit from mental health support alongside recovery programs.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• NAMI Helpline: 1-800-950-6264</li>
                  <li>• MentalHealth.gov</li>
                  <li>• Psychology Today Therapist Finder</li>
                  <li>• Online therapy options available</li>
                </ul>
                <a
                  href="https://www.mentalhealth.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors duration-200 font-semibold"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Learn More
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Remember: Recovery is Possible
          </h2>
          <p className="text-xl text-teal-50 mb-6">
            Millions of people are in recovery today. With support, you can build a life you love.
          </p>
          <p className="text-lg text-teal-100">
            Don't wait. Reach out today. You deserve help and hope.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
